import { 
  Zap, 
  Shield, 
  BarChart3, 
  Smartphone, 
  Globe, 
  Cpu,
  CheckCircle,
  ArrowRight,
  Layers,
  Settings,
  LineChart
} from 'lucide-react'
import { Link } from 'react-router-dom'

const platformFeatures = [
  {
    icon: Globe,
    title: '互联互通',
    description: '对接多家充电运营商和第三方平台，实现跨平台互联互通，为用户提供一站式充电服务体验。',
  },
  {
    icon: Cpu,
    title: '直联管控',
    description: '支持直连充电设备，实现设备级别的精细化管控，提升运营效率和设备利用率。',
  },
  {
    icon: BarChart3,
    title: '数据分析',
    description: '全面的数据采集和分析能力，提供运营报表、用户画像、收益分析等多维度数据洞察。',
  },
  {
    icon: Smartphone,
    title: '多端支持',
    description: '支持Web端、移动端、小程序等多端访问，随时随地管理充电业务。',
  },
  {
    icon: Shield,
    title: '安全可靠',
    description: '完善的安全防护体系，保障数据和交易安全，支持多种支付方式。',
  },
  {
    icon: Zap,
    title: '智能调度',
    description: '基于AI算法的智能调度系统，优化充电资源配置，提升用户体验和运营收益。',
  },
]

const platformModules = [
  {
    icon: Layers,
    title: '运营管理模块',
    features: ['站点管理', '设备管理', '订单管理', '财务管理', '用户管理'],
    color: 'from-neon-cyan to-neon-blue',
  },
  {
    icon: Settings,
    title: '运维管理模块',
    features: ['远程监控', '故障预警', '工单管理', '巡检管理', '备件管理'],
    color: 'from-neon-green to-emerald-400',
  },
  {
    icon: LineChart,
    title: '数据分析模块',
    features: ['运营报表', '收益分析', '用户分析', '设备分析', '趋势预测'],
    color: 'from-neon-purple to-violet-400',
  },
]

const advantages = [
  {
    title: '技术先进',
    description: '采用云原生架构，支持高并发、高可用，系统稳定可靠',
  },
  {
    title: '快速部署',
    description: '标准化部署流程，快速上线，缩短项目周期',
  },
  {
    title: '灵活定制',
    description: '支持个性化定制，满足不同客户的特殊需求',
  },
  {
    title: '持续迭代',
    description: '持续的产品迭代升级，紧跟行业发展趋势',
  },
]

export default function Solutions() {
  return (
    <div className="overflow-hidden bg-dark">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0">
          <div className="absolute inset-0 radial-glow opacity-50" />
          <div className="absolute inset-0 grid-pattern opacity-30" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-6">
              <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
              <span className="text-neon-green text-sm font-medium">解决方案</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              壹电利充<span className="text-gradient">聚合运营管理平台</span>
            </h1>
            <p className="text-lg text-white/50 leading-relaxed">
              自主研发的智能充电运营管理平台，兼具互联互通与直联设备管控能力，助力运营商实现全流程数字化管理
            </p>
          </div>
        </div>
      </section>

      {/* Platform Intro */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-6">
                <Cpu className="w-4 h-4 text-neon-green" />
                <span className="text-neon-green text-sm font-medium">平台介绍</span>
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                智能充电运营管理的<span className="text-gradient">数字化解决方案</span>
              </h2>
              <div className="space-y-4 text-white/50 leading-relaxed">
                <p>
                  "壹电利充"是壹电利科技自主研发的新能源汽车充电聚合运营管理平台，致力于为充电运营商提供一站式的数字化管理解决方案。
                </p>
                <p>
                  平台兼具互联互通与直联设备管控双重能力，既支持对接多家充电运营商和第三方平台实现跨平台互联互通，又支持直连充电设备进行精细化管控。
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3 mt-8">
                {['互联互通', '直联管控', '数据分析', '智能运维'].map((tag) => (
                  <div key={tag} className="flex items-center space-x-2 px-4 py-2 glass-card rounded-full">
                    <CheckCircle className="w-4 h-4 text-neon-green" />
                    <span className="text-sm text-white/70">{tag}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden glass-card neon-border">
                <img
                  src="https://down.dingtalk.com/media/lQLPM50laPly4HnNAuDNBSCwMas5BvfqFFsJscCFwwJ5AA_1312_736.png"
                  alt="壹电利充聚合运营管理平台"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-neon-green/10 rounded-full blur-[60px]" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-neon-cyan/10 rounded-full blur-[40px]" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Zap className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">核心功能</span>
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              平台核心<span className="text-gradient">能力</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              全方位的功能模块，满足充电运营商的各种业务场景需求
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((feature) => (
              <div
                key={feature.title}
                className="glass-card p-6 hover:border-neon-green/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mb-4 group-hover:bg-neon-green group-hover:scale-110 transition-all">
                  <feature.icon className="w-6 h-6 text-neon-green group-hover:text-dark transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Layers className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">功能模块</span>
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              三大核心<span className="text-gradient">模块</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              覆盖充电运营全业务流程，提供完整的管理工具和数据支撑
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {platformModules.map((module) => (
              <div
                key={module.title}
                className="glass-card p-8 hover:border-neon-green/30 transition-all duration-300 group"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform neon-glow`}>
                  <module.icon className="w-7 h-7 text-dark" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{module.title}</h3>
                <ul className="space-y-3">
                  {module.features.map((feature) => (
                    <li key={feature} className="flex items-center space-x-3">
                      <CheckCircle className="w-4 h-4 text-neon-green flex-shrink-0" />
                      <span className="text-white/60 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Shield className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">平台优势</span>
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              为什么选择<span className="text-gradient">壹电利充</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {advantages.map((advantage) => (
              <div
                key={advantage.title}
                className="glass-card p-6 hover:border-neon-green/30 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mb-4 group-hover:bg-neon-green group-hover:scale-110 transition-all">
                  <CheckCircle className="w-6 h-6 text-neon-green group-hover:text-dark transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{advantage.title}</h3>
                <p className="text-white/50 text-sm">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-50" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            立即体验壹电利充平台
          </h2>
          <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">
            联系我们获取平台演示，了解如何通过数字化手段提升您的充电运营效率
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-neon-green text-dark rounded-xl font-semibold hover:bg-neon-green/90 transition-all hover:scale-105 neon-glow-strong"
            >
              申请演示
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-4 glass-card text-white rounded-xl font-semibold hover:bg-white/10 transition-all"
            >
              了解服务详情
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
